/* ========== CELL-REFERENCE & ZIP-PATH HELPERS ==========
 * Parsing A1 references, sheet!A1:B5 formulas, and resolving relative
 * zip-paths from one part of the package to another.
 * ======================================================== */

/**
 * Resolve a relative .rels Target like "../charts/chart1.xml" against the
 * .rels file it came from. Always returns a canonical "xl/..." style path.
 */
export function resolveZipPath(basePath, target) {
  if (!target) return null;
  if (target.startsWith("/")) return target.replace(/^\//, "");
  const baseDir = basePath.replace(/\/[^/]+$/, "");
  const baseRoot = baseDir.replace(/\/_rels$/, "");
  const segments = baseRoot.split("/");
  const rest = target.split("/");
  for (const seg of rest) {
    if (seg === "..") segments.pop();
    else if (seg !== ".") segments.push(seg);
  }
  return segments.join("/");
}

/**
 * Parse an A1-style reference like "C12" or "$C$12" into { row, col }
 * zero-indexed. Returns null on malformed input.
 */
export function parseCellRef(ref) {
  if (!ref) return null;
  const m = ref.match(/^\$?([A-Z]+)\$?(\d+)$/i);
  if (!m) return null;
  const letters = m[1].toUpperCase();
  let col = 0;
  for (let i = 0; i < letters.length; i++) col = col * 26 + (letters.charCodeAt(i) - 64);
  return { row: parseInt(m[2], 10) - 1, col: col - 1 };
}

/**
 * Parse a fully-qualified range formula like "'Sheet 1'!$A$1:$B$5" into
 * { sheet, tl, br } with zero-indexed cells. Returns null if it can't
 * recognize the format.
 */
export function parseSheetRangeFormula(formula) {
  if (!formula) return null;
  const trimmed = formula.trim().replace(/^=/, "");
  const m = trimmed.match(/^(?:'([^']+)'|([^!]+))!(.+)$/);
  if (!m) return null;
  const sheet = m[1] || m[2];
  const rangePart = m[3];
  const [tlRef, brRef] = rangePart.split(":");
  const tl = parseCellRef(tlRef);
  const br = brRef ? parseCellRef(brRef) : tl;
  if (!tl) return null;
  return { sheet, tl, br: br || tl };
}
